// PWA install / Add-to-Home-Screen prompt — shared by player / admin /
// agent apps. Redesigned 2026-05-25 (v3 — compact + Android install
// button + icon-annotated steps):
//
//  • COMPACT, BEAUTIFUL card — tight one-glance header (icon + title +
//    one-line hint + corner dismiss), a single action row, and the
//    loved step-by-step COLLAPSIBLE (collapsed by default).
//
//  • ANDROID INSTALL BUTTON — a prominent "Install" button (with icon).
//    When the browser captured a beforeinstallprompt it triggers the
//    real native install on tap; otherwise it reveals the step-by-step
//    guide. The Android steps are written around this button.
//
//  • iOS — Safari's "Add to Home Screen" CANNOT be triggered from a web
//    page (no API exists), so there is no fake button. Instead the iOS
//    primary button reveals very clear, icon-annotated steps, and a
//    Listen button reads them aloud.
//
//  • ICON-ANNOTATED STEPS — each step that references an on-screen
//    control shows the ACTUAL glyph the user will look for:
//      ios_share  → the Share button (a square with an up arrow)
//      add_box    → "Add to Home Screen" (a rounded box with a +)
//      install_mobile / more_vert → Android install / browser menu
//    so users recognise the buttons visually, not just by name.
//
//  • LISTEN (TTS) — reads the full descriptive script in the user's
//    chosen language (all 12). Toggles to Stop; stops on dismiss.
//
//  • SUPPRESSION — never shown once installed / added to home screen
//    (display-mode standalone OR navigator.standalone), nor for a week
//    after a dismissal; the appinstalled event also hides it.

import { useEffect, useRef, useState } from 'react';
import { useI18n } from './i18n';
import { speak, stopTts, isTtsSupported, onTtsStateChange, type TtsLang } from './tts';
import Icon from '../components/Icon';

type BIPEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

const KEYS = {
  player: 'pwa.installPromptDismissedAt',
  admin:  'pwa.installPromptDismissedAt.admin',
  agent:  'pwa.installPromptDismissedAt.agent',
};
const REPROMPT_MS = 7 * 24 * 60 * 60 * 1000; // a week

function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
    // @ts-ignore iOS Safari home-screen flag
    window.navigator.standalone === true;
}
function isIosDevice() {
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return true;
  // iPadOS 13+ reports as a Mac; distinguish by touch support.
  // @ts-ignore maxTouchPoints exists on modern navigators
  return navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
}
function isIosSafari() {
  return isIosDevice() && !/crios|fxios|edgios|opios/i.test(navigator.userAgent);
}

type Variant = 'player' | 'admin' | 'agent';
type Platform = 'iosSafari' | 'iosOther' | 'android';

const APP_META: Record<Variant, { app: string; iconSrc: string }> = {
  player: { app: 'Baali Games', iconSrc: '/icons/icon-96.png' },
  admin:  { app: 'BGs Admin',   iconSrc: '/icons-admin/icon-96.png' },
  agent:  { app: 'BGs Agent',   iconSrc: '/icons-agent/icon-96.png' },
};

// Per-step glyphs (by step index, language-independent — the translated
// step ORDER is identical across all 12 dicts). null → show the number.
const STEP_ICONS: Record<Platform, (string | null)[]> = {
  iosSafari: ['ios_share', null, 'add_box', null],
  iosOther:  [null, 'public', 'ios_share', 'add_box'],
  android:   ['install_mobile', 'check_circle', 'home', 'more_vert'],
};

export default function InstallPrompt({ variant = 'player' }: { variant?: Variant }) {
  const key = KEYS[variant];
  const meta = APP_META[variant];
  const { lang, t } = useI18n();

  const [deferred, setDeferred] = useState<BIPEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [platform, setPlatform] = useState<Platform>('android');
  const [expanded, setExpanded] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  // The speech engine (esp. Indian-language voices) can take a moment to
  // warm up before it actually starts talking. `preparing` shows an
  // instant spinner on tap so the press registers, then flips to the
  // "speaking" state the moment audio begins.
  const [preparing, setPreparing] = useState(false);
  const prepareTimer = useRef<number | undefined>(undefined);

  function dismissedRecently() {
    const v = Number(localStorage.getItem(key) || 0);
    return v && Date.now() - v < REPROMPT_MS;
  }

  useEffect(() => {
    if (isStandalone() || dismissedRecently()) return;
    setPlatform(isIosDevice() ? (isIosSafari() ? 'iosSafari' : 'iosOther') : 'android');

    function onBIP(e: Event) {
      e.preventDefault();
      setDeferred(e as BIPEvent);
      setVisible(true);
    }
    window.addEventListener('beforeinstallprompt', onBIP);

    let t1: number | undefined;
    if (isIosDevice()) t1 = window.setTimeout(() => setVisible(true), 1500);
    const t2 = window.setTimeout(() => {
      if (!isIosDevice() && !dismissedRecently()) setVisible(true);
    }, 2500);

    const onInstalled = () => {
      localStorage.setItem(key, String(Date.now()));
      setVisible(false);
    };
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
      window.removeEventListener('appinstalled', onInstalled);
      if (t1) clearTimeout(t1);
      clearTimeout(t2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    const off = onTtsStateChange((v) => {
      setSpeaking(v);
      if (v) setPreparing(false);   // audio actually started → drop spinner
    });
    return () => {
      off();
      stopTts();
      if (prepareTimer.current) clearTimeout(prepareTimer.current);
    };
  }, []);

  if (!visible) return null;

  const isAndroid = platform === 'android';

  const dismiss = () => {
    stopTts();
    localStorage.setItem(key, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) { setExpanded(true); return; } // no native prompt → show the steps
    stopTts();
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === 'accepted' || outcome === 'dismissed') {
      localStorage.setItem(key, String(Date.now()));
    }
    setVisible(false);
  };

  const title    = t('pwa.install.title', { app: meta.app });
  const subhint  = t('pwa.install.subhint');
  const guideKey = platform === 'iosSafari'
    ? 'pwa.install.guide.iosSafari'
    : platform === 'iosOther'
      ? 'pwa.install.guide.iosOther'
      : 'pwa.install.guide.android';
  const steps = t(guideKey).split('\n').map((s) => s.trim()).filter(Boolean);
  const stepIcons = STEP_ICONS[platform] || [];

  const toggleListen = () => {
    if (speaking || preparing) {           // tap again = cancel
      stopTts();
      setPreparing(false);
      if (prepareTimer.current) clearTimeout(prepareTimer.current);
      return;
    }
    setPreparing(true);                    // instant feedback on press
    const ok = speak(`${title}. ${subhint} ${steps.join('. ')}`, lang as TtsLang);
    if (!ok) { setPreparing(false); return; }
    // Safety: if the engine never fires onstart (no voice / blocked),
    // clear the spinner so it can't spin forever.
    if (prepareTimer.current) clearTimeout(prepareTimer.current);
    prepareTimer.current = window.setTimeout(() => setPreparing(false), 6000);
  };
  const toggleSteps = () => setExpanded((v) => !v);
  const ttsOk = isTtsSupported();

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] px-3 pointer-events-none"
         style={{ paddingBottom: 'calc(0.6rem + env(safe-area-inset-bottom))' }}>
      <div
        role="dialog"
        aria-label={title}
        className="pointer-events-auto relative max-w-sm mx-auto rounded-2xl overflow-hidden
                   bg-white/95 dark:bg-arena-800/95 backdrop-blur-xl
                   ring-1 ring-arena-200/80 dark:ring-neon-400/20
                   shadow-[0_12px_40px_rgba(0,0,0,0.18)] animate-slide-down"
      >
        {/* Thin accent bar — premium touch without bulk. */}
        <div className="h-1 bg-gradient-to-r from-neon-400 via-brand-400 to-neon-400" />

        {/* Header — single tight row. */}
        <div className="px-3.5 pt-3 pb-2.5 flex items-center gap-3">
          <img src={meta.iconSrc} alt="" className="w-10 h-10 rounded-xl shrink-0 shadow-sm ring-1 ring-black/5" />
          <div className="min-w-0 flex-1 pr-6">
            <div className="font-display font-extrabold text-[14.5px] leading-tight text-arena-900 dark:text-neon-100 truncate">
              {title}
            </div>
            <div className="text-[11px] leading-snug text-arena-500 dark:text-neon-200/70 line-clamp-2">
              {subhint}
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label={t('pwa.install.notNow')}
            title={t('pwa.install.notNow')}
            className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full grid place-items-center
                       text-arena-400 dark:text-neon-200/60
                       hover:bg-arena-100/70 dark:hover:bg-arena-700/70 transition"
          >
            <Icon name="close" size={17} />
          </button>
        </div>

        {/* Action row — primary CTA + Listen. Below the copy, so text is
            never covered. On Android the primary installs; on iOS it
            reveals the steps (Safari add-to-home can't be web-triggered). */}
        <div className="px-3.5 pb-3 flex items-center gap-2">
          <button
            type="button"
            onClick={isAndroid ? install : toggleSteps}
            // Light bluish fill + BLACK text (readable, the old bright
            // neon hid the label) + an always-on ORANGE border so it pops
            // in both light and dark mode.
            className="flex-1 inline-flex items-center justify-center gap-1.5
                       px-3 py-2.5 rounded-xl text-[13.5px] font-extrabold
                       text-neutral-900
                       bg-neon-200 hover:bg-neon-300
                       border-2 border-orange-500
                       active:scale-[.98] transition
                       shadow-[0_2px_10px_rgba(249,115,22,0.20)]"
          >
            <Icon name={isAndroid ? 'install_mobile' : 'add_box'} size={18} filled />
            {isAndroid
              ? t('pwa.install.installBtn')
              : (expanded ? t('pwa.install.howToHide') : t('pwa.install.howToShow'))}
          </button>
          {ttsOk && (
            <button
              type="button"
              onClick={toggleListen}
              aria-pressed={speaking || preparing}
              aria-label={speaking ? t('pwa.install.listening') : t('pwa.install.listen')}
              title={speaking ? t('pwa.install.listening') : t('pwa.install.listen')}
              className={`shrink-0 inline-flex items-center justify-center gap-1.5
                          h-[42px] px-3 rounded-xl text-[13px] font-bold transition active:scale-[.98]
                          ${speaking
                            ? 'bg-rose-500 text-white shadow-[0_3px_12px_rgba(244,63,94,0.35)]'
                            : preparing
                              ? 'bg-amber-400 text-neutral-900 shadow-[0_3px_12px_rgba(245,158,11,0.35)]'
                              : 'bg-arena-100 dark:bg-arena-700/60 text-arena-800 dark:text-neon-100 ring-1 ring-arena-200 dark:ring-arena-600/40'}`}
            >
              <Icon
                name={preparing ? 'progress_activity' : (speaking ? 'stop_circle' : 'volume_up')}
                size={18}
                filled
                className={preparing ? 'animate-spin' : ''}
              />
              <span>{speaking ? t('pwa.install.listening') : t('pwa.install.listen')}</span>
            </button>
          )}
        </div>

        {/* Collapsible "How to install" — Android gets its own toggle row
            (the Install button installs rather than expands); on iOS the
            primary button already toggles, so the row is hidden there. */}
        {isAndroid && (
          <button
            type="button"
            onClick={toggleSteps}
            aria-expanded={expanded}
            className="w-full px-3.5 py-2 flex items-center justify-between gap-2
                       border-t border-arena-200/70 dark:border-arena-700/40
                       text-[12px] font-bold text-arena-600 dark:text-neon-200/80
                       hover:bg-arena-50/70 dark:hover:bg-arena-700/40 transition"
          >
            <span className="inline-flex items-center gap-1.5">
              <Icon name="help" size={15} className="text-neon-600 dark:text-neon-300" />
              {expanded ? t('pwa.install.howToHide') : t('pwa.install.howToShow')}
            </span>
            <Icon name="expand_more" size={19}
                  className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
        )}

        {/* Steps — icon-annotated so users recognise each control. */}
        {expanded && (
          <ol className={`px-3.5 pb-4 pt-2 space-y-2.5 list-none
                          ${isAndroid ? '' : 'border-t border-arena-200/70 dark:border-arena-700/40 mt-0'}`}>
            {steps.map((step, i) => {
              const glyph = stepIcons[i];
              return (
                <li key={i} className="flex items-start gap-2.5">
                  <span className={`shrink-0 w-7 h-7 rounded-lg grid place-items-center
                                    ${glyph
                                      ? 'bg-neon-100 text-neon-700 dark:bg-neon-400/20 dark:text-neon-200 ring-1 ring-neon-300/50 dark:ring-neon-400/30'
                                      : 'bg-arena-100 text-arena-600 dark:bg-arena-700/60 dark:text-neon-200/80 text-[11px] font-extrabold'}`}>
                    {glyph ? <Icon name={glyph} size={17} filled /> : i + 1}
                  </span>
                  <span className="text-[12.5px] leading-relaxed text-arena-700 dark:text-neon-200/85 pt-0.5">
                    {step}
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </div>
  );
}
